// src/services/listingService.js
import api from '../api';

/**
 * Service for handling listing operations
 */
export const listingService = {
  /**
   * Get all listings for a facility
   * @param {string} facilityId - The ID of the facility
   * @param {string} token - Authentication token
   * @returns {Promise} Promise with the listings data
   */
  getListingsForFacility: async (facilityId, token) => {
    try {
      const response = await api.get(`/facilities/${facilityId}/listings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response && response.data) {
        return Array.isArray(response.data) ? response.data : (response.data.data || []);
      }
      return [];
    } catch (error) {
      console.error('Error fetching listings for facility:', error);
      throw error;
    }
  },

  /**
   * Create a new listing for a facility
   * @param {string} facilityId - The ID of the facility
   * @param {Object} listingData - The listing data to create
   * @param {string} token - Authentication token
   * @returns {Promise} Promise with the created listing data
   */
  createListing: async (facilityId, listingData, token) => {
    return api.post(`/facilities/${facilityId}/listings`, listingData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },

  /**
   * Update a listing
   * @param {string} facilityId - The ID of the facility
   * @param {string} listingId - The ID of the listing to update
   * @param {Object} updateData - The listing data to update
   * @param {string} token - Authentication token
   * @returns {Promise} Promise with the updated listing data
   */
  updateListing: async (facilityId, listingId, updateData, token) => {
    return api.put(`/facilities/${facilityId}/listings/${listingId}`, updateData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },

  /**
   * Delete a listing
   * @param {string} facilityId - The ID of the facility
   * @param {string} listingId - The ID of the listing to delete
   * @param {string} token - Authentication token
   * @returns {Promise} Promise with the result of the deletion
   */
  deleteListing: async (facilityId, listingId, token) => {
    return api.delete(`/facilities/${facilityId}/listings/${listingId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  },

  /**
   * Update a specific field on a listing
   * @param {string} facilityId - The ID of the facility
   * @param {string} listingId - The ID of the listing to update
   * @param {string} fieldPath - The field name (can be nested using dot notation e.g., 'compensation.amount')
   * @param {*} value - The new value for the field
   * @param {string} token - Authentication token
   * @returns {Promise} Promise with the updated listing data
   */
  updateListingField: async (facilityId, listingId, fieldPath, value, token) => {
    // Create an object with the nested field structure
    const updateData = {};
    
    if (fieldPath.includes('.')) {
      const parts = fieldPath.split('.');
      let current = updateData;
      
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = {};
        current = current[parts[i]];
      }
      
      current[parts[parts.length - 1]] = value;
    } else {
      updateData[fieldPath] = value;
    }
    
    return api.put(`/facilities/${facilityId}/listings/${listingId}`, updateData, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  }
};

export default listingService;
